import styled, { css } from "styled-components";
import { pxToRem, withAlpha } from "../../styles/utils";
import type {
  StatusBadgeAppearance,
  StatusBadgeVariant,
} from "./StatusBadge.types";

const softVariant = (variant: StatusBadgeVariant) => {
  if (variant === "green") {
    return css`
      border: 0;
      background: ${({ theme }) => withAlpha(theme.colors.success, 0.14)};
      color: ${({ theme }) => theme.colors.successText};
    `;
  }
  if (variant === "red") {
    return css`
      border: 0;
      background: ${({ theme }) => withAlpha(theme.colors.dangerText, 0.1)};
      color: ${({ theme }) => theme.colors.dangerText};
    `;
  }
  if (variant === "yellow") {
    return css`
      border: 0;
      background: ${({ theme }) => withAlpha(theme.colors.warning, 0.16)};
      color: ${({ theme }) => theme.colors.warningText};
    `;
  }
  if (variant === "brand") {
    return css`
      border: 0;
      background: ${({ theme }) => theme.colors.brandSubtle};
      color: ${({ theme }) => theme.colors.brandDark};
    `;
  }
  return css`
    border: 0;
    background: ${({ theme }) => theme.colors.inkSurface};
    color: ${({ theme }) => theme.colors.inkMuted};
  `;
};

const outlinedVariant = (variant: StatusBadgeVariant) => {
  if (variant === "green") {
    return css`
      border: ${pxToRem(1)} solid ${({ theme }) => withAlpha(theme.colors.success, 0.4)};
      background: ${({ theme }) => withAlpha(theme.colors.success, 0.1)};
      color: ${({ theme }) => theme.colors.success};
    `;
  }
  if (variant === "red") {
    return css`
      border: ${pxToRem(1)} solid ${({ theme }) => withAlpha(theme.colors.danger, 0.4)};
      background: ${({ theme }) => withAlpha(theme.colors.danger, 0.1)};
      color: ${({ theme }) => theme.colors.danger};
    `;
  }
  if (variant === "yellow") {
    return css`
      border: ${pxToRem(1)} solid ${({ theme }) => withAlpha(theme.colors.warning, 0.45)};
      background: ${({ theme }) => withAlpha(theme.colors.warning, 0.12)};
      color: ${({ theme }) => theme.colors.warningText};
    `;
  }
  if (variant === "brand") {
    return css`
      border: ${pxToRem(1)} solid ${({ theme }) => withAlpha(theme.colors.brand, 0.4)};
      background: ${({ theme }) => theme.colors.brandSubtle};
      color: ${({ theme }) => theme.colors.brand};
    `;
  }
  return css`
    border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.inkMuted};
  `;
};

export const StyledBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => !["variant", "appearance", "asButton"].includes(prop),
})<{
  variant: StatusBadgeVariant;
  appearance: StatusBadgeAppearance;
  asButton: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: max-content;
  min-height: ${pxToRem(32)};
  height: auto;
  padding: ${pxToRem(8)} ${pxToRem(14)};
  border-radius: ${pxToRem(8)};
  font-size: ${pxToRem(13)};
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  cursor: ${({ asButton }) => (asButton ? "pointer" : "default")};
  white-space: nowrap;

  ${({ variant, appearance }) =>
    appearance === "soft" ? softVariant(variant) : outlinedVariant(variant)}

  &:hover {
    ${({ asButton, variant, theme }) =>
      asButton &&
      css`
        background: ${withAlpha(
          variant === "green"
            ? theme.colors.success
            : variant === "yellow"
            ? theme.colors.warning
            : variant === "red"
            ? theme.colors.danger
            : variant === "brand"
            ? theme.colors.brand
            : theme.colors.ink,
          0.16,
        )};
      `}
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;
